import { Suspense, forwardRef, useRef, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { enqueueSnackbar } from "notistack"
import { FormikProps } from "formik"
import { queryKeyFor, useApiQuery } from "../../../../../../core/api/useApiQuery"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { PageHeader } from "../../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../../shared/components/pageHeader/PageHeaderApi"
import { StandardLoadingButton } from "../../../../../../shared/components/buttons/LoadingButton"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../../shared/components/links/StandardBreadcrumbs"
import { QueryErrorBoundary } from "../../../../../../core/api/components/QueryErrorBoundary"
import { LoadingSpinner } from "../../../../../../shared/components/loading/LoadingSpinner"
import { USER_REPORTS_ROUTE, USER_REPORTS_SETTINGS_ROUTE } from "../../../../../../constants/routes"
import { fetchBillReceiverById, updateBillReceiver, UserSettingsQuery } from "../settingsApi"
import { useAppSelector } from "../../../../../store/storeHooks"
import { selectEffectiveUserId } from "../../../../../context/contextSelectors"
import { EditBillReceiverForm, EditBillReceiverFormValues } from "../components/EditBillReceiverForm"
import { BillReceiverEndpointType } from "../settingsTypes"
import { BillReceiversType } from "../../../../../../shared/components/billReceivers/billReceiverTypes"

const BillReceiverForm = forwardRef<FormikProps<EditBillReceiverFormValues>>((_, ref) => {
    const { billReceiverId } = useParams()
    const navigate = useNavigate()
    const userId = useAppSelector(selectEffectiveUserId) || 0

    const billReceiverResult = useApiQuery(fetchBillReceiverById, {
        queryName: UserSettingsQuery.BILL_RECEIVER_DETAILS,
        dependencies: { billReceiverId: Number(billReceiverId) },
        select: (data) => data.billReceiver,
        suspense: true,
        useErrorBoundary: true,
    })

    const { mutateAsync: callUpdateBillReceiver } = useApiMutation(updateBillReceiver, {
        async onSuccess(params, data, context) {
            context.queryClient.invalidateQueries({
                queryKey: queryKeyFor(UserSettingsQuery.BILL_RECEIVER_DETAILS, {
                    billReceiverId: params.billReceiverId,
                }),
            })
            enqueueSnackbar({
                variant: "success",
                message: data.serviceResponse.displayMessage,
            })
            navigate(USER_REPORTS_SETTINGS_ROUTE)
        },
    })

    const handleSubmit = async (values: EditBillReceiverFormValues) => {
        if (!billReceiverResult.data) return

        const isEan = values.endpointType === BillReceiverEndpointType.EAN
        const isPrivateCompany = values.billType === BillReceiversType.PRIVATE_COMPANY

        await callUpdateBillReceiver({
            billReceiverId: Number(billReceiverId),
            payload: {
                billReceiver: {
                    type: values.billType,
                    ...(isPrivateCompany && { vatnumber: values.vatnumber }),
                    debitorNo: values.debtorNumber || undefined,
                    name: values.name,
                    address: {
                        country: { countryCode: values.country },
                        postalCode: values.postalCode,
                        city: values.city,
                        addressLines: [values.addressLine1, values.addressLine2].filter(Boolean),
                    },
                    endpoint: isEan
                        ? { type: BillReceiverEndpointType.EAN, ean: values.endpointValue }
                        : { type: BillReceiverEndpointType.EMAIL, email: values.endpointValue },
                    isActive: values.isActive,
                    contactDetails: {
                        name: values.contactPerson,
                        email: values.email,
                        phoneNumber: values.telephone,
                    },
                },
                onBehalfOf: { userId },
            },
        })
    }

    return (
        <EditBillReceiverForm
            ref={ref}
            billReceiver={billReceiverResult.data ?? null}
            onSubmit={handleSubmit}
        />
    )
})

export function EditBillReceiverPage() {
    const formRef = useRef<FormikProps<EditBillReceiverFormValues>>(null)
    const [isSubmitting, setIsSubmitting] = useState(false)

    const handleSaveClick = async () => {
        setIsSubmitting(true)
        try {
            await formRef.current?.submitForm()
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <>
            <LocalizedStrict id="user-settings-edit-bill-receiver-page-header" attrs={{ title: true }}>
                <PageHeader
                    page={Page.USER_EDIT_BILL_RECEIVER}
                    title="Edit bill receiver"
                    rightElement={
                        <LocalizedStrict id="user-settings-edit-bill-receiver-save-button">
                            <StandardLoadingButton type="submit" onClick={handleSaveClick} loading={isSubmitting}>
                                Update
                            </StandardLoadingButton>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="user-nav-item-home">
                            <BreadcrumbLink to={USER_REPORTS_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="user-nav-item-settings">
                            <BreadcrumbLink to={USER_REPORTS_SETTINGS_ROUTE}>Settings</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="user-nav-item-edit-bill-receiver">
                            <Breadcrumb>Edit bill receiver</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>
            <QueryErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                    <BillReceiverForm ref={formRef} />
                </Suspense>
            </QueryErrorBoundary>
        </>
    )
}
